import { AppContext, assets, CheckboxTable, CheckboxTableColum, DatePicker, ErrorHandling, Input, OPLReportService, Permission, PulseOpsFormat, SelectSearch, sharedStyle, SortFieldInfo } from '@pulseops/common'
import { SectionContent, SectionCol, SectionRow } from '@pulseops/submission/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { OPLSharedStyles } from '../opl-common'
import { Controller, useForm } from 'react-hook-form'
import { OPLReportForm } from './OPLReportForm'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useLoading } from '@mxt/zio-react'
import moment from 'moment'
import { OPLStackContext } from '../OPLStackContext'

export const OPLReport = () => {
  const { t } = useTranslation()
  const isFocused = useIsFocused()
  const [isLoading, bindLoader] = useLoading(false)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [total, setTotal] = React.useState<number>(0)
  const [sortItem, setSortItem] = React.useState<SortFieldInfo>({
    field: 'printingDate',
    order: "desc"
  })
  const [searchedList, setSearchedList] = React.useState<OPLReportService.OPLReportInfo[]>([])
  const { changeBreadcrumb, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const rootNavigation = useNavigation()
  const { userPermissions } = React.useContext(OPLStackContext)
  const reportForm = useForm<OPLReportForm.OPLReportSearchedData>({
    defaultValues: {
      policyNumber: '',
      clientName: '',
      letterType: undefined,
      fileName: '',
      printingDate: null
    },
    mode: 'onChange'
  })
  const displayedColumns: CheckboxTableColum[] = [
    {
      title: t('OPL_CS:OPL_Report:PolicyNumber'),
      fieldName: 'policyNum',
      disabled: true
    },
    {
      title: t('submission:ClientName'),
      fieldName: 'clientName',
      disabled: true
    },
    {
      title: t('OPL_CS:OPL_Report:LetterType'),
      fieldName: 'letterType',
      disabled: true
    },
    {
      title: t('OPL_CS:OPL_Report:FileName'),
      fieldName: 'fileName',
      disabled: true
    },
    {
      title: t('OPL_CS:OPL_Report:PrintingDate'),
      fieldName: 'printingDate',
      rowRender: (selectedRow) => {
        const printingDate = selectedRow ? selectedRow.selectItem["printingDate"] as string : ''
        return <Text>{PulseOpsFormat.dateStringtoFormat(printingDate, 'DD/MM/YYYY')}</Text>
      }
    }
  ]

  const letterTypeOptions = pipe(
    OPLReportService.getLetterTypeList(),
    ErrorHandling.runDidMount()
  ) || []

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            rootNavigation.navigate('HomeScreen')
          }
        },
        {
          title: t('menu:OPL'),
          navigate: () => rootNavigation.navigate('OPLScreen')
        },
        {
          title: t('OPL_CS:OPL_Report:Report'),
          navigate: null
        }
      ])
      getOPLReportList(0, 10)
    }
    return () => {
      setTotal(0)
      setSearchedList([])
      reportForm.reset({
        policyNumber: '',
        clientName: '',
        letterType: undefined,
        fileName: '',
        printingDate: null
      })
      setSortItem({
        field: 'printingDate',
        order: "desc"
      })
    }
  }, [isFocused])

  React.useEffect(() => { showGlobalLoading(isLoading) }, [isLoading])

  const getOPLReportList = (pageNo: number, pageSi: number) => {
    const formData = reportForm.getValues()
    const printingDate = formData.printingDate ? PulseOpsFormat.serviceInquiry_endOfDate(formData.printingDate, true).toISOString() : ''
    const submitedData: OPLReportService.OPLReportRequest = {
      start: pageNo,
      size: pageSi,
      sort: "DESC",
      order: "printingDate",
      policyNum: formData.policyNumber,
      clientName: formData.clientName,
      letterType: formData.letterType?.value || '',
      fileName: formData.fileName || '',
      printingDate: printingDate
    }
    pipe(
      OPLReportService.getOPLReportList(submitedData),
      ZIO.map((response) => {
        if (response && response.data && response.data.length > 0) {
          setTotal(response.total)
          setSearchedList(response.data)
        } else {
          setTotal(0)
          setSearchedList([])
        }
        return response
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const onSearchEvent = async () => {
    const isValidForm = await reportForm.trigger()
    if (isValidForm) {
      setPageNum(0)
      setPageSize(10)
      getOPLReportList(0, 10)
    }
  }

  const onPageChange = (pageNo: number, pageSi: number) => {
    setPageNum(pageNo)
    getOPLReportList(pageNo, pageSi)
  }
  const onRowsPerPageChange = (pageNo: number, pageSi: number) => {
    setPageSize(pageSi)
    getOPLReportList(pageNo, pageSi)
  }
  return (
    <ScrollView style={OPLReportStyles.container}>
      <SectionContent>
        <SectionRow>
          <SectionCol>
            <Controller
              control={reportForm.control}
              name={"policyNumber"}
              render={({ field: { value, onChange, onBlur } }) => (
                <Input
                  title={t('OPL_CS:OPL_Report:PolicyNumber')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxLength={10}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={reportForm.control}
              name={"clientName"}
              render={({ field: { value, onChange, onBlur } }) => (
                <Input
                  title={t('submission:ClientName')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={reportForm.control}
              name={"letterType"}
              render={({ field: { value, onChange, onBlur } }) => (
                <SelectSearch
                  label={t('OPL_CS:OPL_Report:LetterType')}
                  options={letterTypeOptions.map((item) => ({ label: item.name, value: item.name }))}
                  value={value}
                  onChange={onChange}
                  popupIcon={<assets.ArrowDownDropdownIcon />}
                  placeholder={t('common:Select')}
                />
              )}
            />
          </SectionCol>
        </SectionRow>
        <SectionRow sectionStyles={OPLSharedStyles.secondLine}>
          <SectionCol>
            <Controller
              control={reportForm.control}
              name={"fileName"}
              render={({ field: { value, onChange, onBlur } }) => (
                <Input
                  title={t('OPL_CS:OPL_Report:FileName')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={reportForm.control}
              name={"printingDate"}
              rules={{
                validate: (val) => {
                  if (val && !moment(val).isValid()) {
                    return `${t('message:MS020081')}`
                  } else {
                    return true
                  }
                }
              }}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <DatePicker
                  alwaysShow
                  label={t('OPL_CS:OPL_Report:PrintingDate')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={new Date()}
                  maxDateMessage={t('message:MS990032')}
                  minDate={undefined}
                  minDateMessage={''}
                  errorMessage={error?.message}
                  labelStyle={OPLSharedStyles.datePickerText}
                />
              )}
            />
          </SectionCol>
        </SectionRow>
        {userPermissions.includes(Permission.SearchOPLSDViewLogHardcopy) && (
          <View style={[OPLReportStyles.buttonContainer, OPLSharedStyles.secondLine]}>
            <TouchableOpacity style={[sharedStyle.button, sharedStyle.btnRed, OPLReportStyles.buttonContent]} onPress={() => onSearchEvent()}>
              <Text style={[sharedStyle.textButtonRed]}>{t('common:Search')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </SectionContent>
      <CheckboxTable
        displayedColumns={displayedColumns}
        dataSource={searchedList}
        stickyHeader
        isPagination={true}
        totalRecord={total}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        setPageSize={setPageSize}
        setTotalRecord={setTotal}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sortItem={sortItem}
        setSortItem={setSortItem}
      />
    </ScrollView>
  )
}
const OPLReportStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E7E7E7',
    paddingHorizontal: 20
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  buttonContent: {
    marginVertical: 0,
    marginLeft: 0
  },
})